import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import { Chip, Stack, Tooltip } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { m } from 'framer-motion';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { getFeaturePermission } from '@zeta-commons';
import {
  CustomPopover,
  hasActiveSubscriptionPlans,
  usePopover,
  useProfileProgressStore,
  useProfileStore,
  useSettingsContext,
  useRouter,
  useSnackbar,
  useSubscriptionDetailsStore,
  useUserAccessStore,
  useUserDetailsStore,
  varHover,
} from '@zeta-core';
import { GearIcon, LogOutIcon } from '@zeta-core/assets';
import { AdvisorAPIs, DocumentService } from '@zeta-services';
import { IDocumentDownloadRequest, IEmptyRequestResponse, IGetPersonalProfileResponse } from '@zeta-types';
import { RouterLinks } from '../../app-route-paths';
import { signOut } from '../../auth/jwt';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const isAccessible = getFeaturePermission(userAccess?.access, 'AdvisorOrgConfig').accessible;
  const router = useRouter();
  const theme = useTheme();
  const navigate = useNavigate();
  const settings = useSettingsContext();
  const { firstName, lastName, orgRole } = useUserDetailsStore((state) => state.user);
  const profileProgressStore = useProfileProgressStore((state) => state);
  const profilePic = useProfileStore((state) => state.profilePic);
  const newProfilePic = useProfileStore((state) => state.newProfilePic);
  const updateProfilePicStore = useProfileStore((state) => state.updateProfilePicStore);
  const currentSubscription = useSubscriptionDetailsStore((state) => state.subscription);
  const hasActivePlan = hasActiveSubscriptionPlans(currentSubscription);

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await signOut();
      profileProgressStore.updateProfileSetupStatus(null, false);
      popover.onClose();
      router.replace('/');
    } catch (error) {
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleSettings = () => {
    popover.setOpen(null);
    if (isAccessible) setTimeout(() => navigate(RouterLinks.organisationProfile), 200);
    else setTimeout(() => navigate(RouterLinks.myProfile), 200);
  };

  const handleThemeToggle = () => {
    popover.onClose();
    settings.onUpdate('themeMode', settings.themeMode === 'dark' ? 'light' : 'dark');
  };

  const useDownloadFileAPI = () => {
    return useMutation({
      mutationFn: (payload: IDocumentDownloadRequest) => DocumentService().downloadDocument(payload),

      onSuccess: (response: AxiosResponse<Blob>) => {
        const { data: responseData } = response;
        const file: string = URL.createObjectURL(responseData);
        updateProfilePicStore({ profilePic: file, file: responseData });
        console.log(file);
      },
    });
  };

  const downloadPersonalProfileLogoAPI = useDownloadFileAPI();

  const getPersonalProfileApi = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) => AdvisorAPIs().getPersonalProfile(payload),
    retry: 1,

    onSuccess: (data: AxiosResponse<IGetPersonalProfileResponse>) => {
      const { data: response } = data;

      if (Object.keys(response.response).length !== 0) {
        if (response.response.individualLogoDocumentID) {
          const payload = {
            documentKey: response.response.individualLogoDocumentID,
          };
          downloadPersonalProfileLogoAPI.mutate(payload);
        }
      }
    },
  });

  useEffect(() => {
    getPersonalProfileApi.mutate({});
  }, []);

  // remove _ and capitalize first letter of each word
  const getOrgRoleName = () => {
    const roles = orgRole?.map((role) =>
      role
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    );
    return roles;
  };

  const visibleRoles = getOrgRoleName()?.slice(0, 2) || [];
  const remainingRoles = getOrgRoleName()?.slice(2) || [];

  return (
    <>
      <IconButton
        component={m.button}
        id="account"
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        {!profilePic && firstName && !newProfilePic && (
          <Avatar
            key={firstName}
            alt={firstName}
            id="avatar"
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.background.default,
              color: theme.palette.action.active,
            }}
          >
            {firstName?.charAt(0).toUpperCase()}
          </Avatar>
        )}
        {profilePic && !newProfilePic && (
          <Avatar
            src={profilePic}
            key={profilePic}
            alt={firstName}
            id="avatar"
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.background.default,
              color: theme.palette.action.active,
            }}
          >
            {profilePic}
          </Avatar>
        )}
        {newProfilePic && (
          <Avatar
            src={newProfilePic}
            key={newProfilePic || firstName}
            alt={firstName}
            id="avatar"
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.background.default,
              color: theme.palette.action.active,
            }}
          >
            {newProfilePic}
          </Avatar>
        )}
      </IconButton>

      <CustomPopover {...popover} sx={{ width: 350, p: 0 }}>
        <Stack
          sx={{
            p: 2,
            pb: 1.5,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
          gap={1}
        >
          {profilePic && !newProfilePic && (
            <Avatar sx={{ width: 62, height: 62, background: theme.palette.grey[300] }} id="profile-icon" src={profilePic} key={profilePic || firstName}>
              {profilePic ? null : (
                <Typography variant="subtitle2" sx={{ color: theme.palette.grey[600] }} id="profile-name-icon">
                  {firstName?.charAt(0).toUpperCase()}
                  {lastName?.charAt(0).toUpperCase()}
                </Typography>
              )}
            </Avatar>
          )}

          {newProfilePic && (
            <Avatar sx={{ width: 62, height: 62, background: theme.palette.grey[300] }} id="profile-icon" src={newProfilePic} key={newProfilePic || firstName}>
              {newProfilePic ? null : (
                <Typography variant="subtitle2" sx={{ color: theme.palette.grey[600] }} id="profile-name-icon">
                  {firstName?.charAt(0).toUpperCase()}
                  {lastName?.charAt(0).toUpperCase()}
                </Typography>
              )}
            </Avatar>
          )}
          <Typography variant="h6" noWrap id="profile-name">
            {firstName} {lastName}
          </Typography>
          {visibleRoles?.length > 0 &&
            visibleRoles?.map((item: string) => (
              <Chip key={JSON.stringify(item)} variant="soft" size="small" label={<Typography variant="button">{item}</Typography>} />
            ))}
          {remainingRoles?.length > 0 && (
            <Tooltip title={remainingRoles?.join(', ')} arrow>
              <Chip variant="soft" size="small" label={<Typography variant="button">+{remainingRoles?.length}</Typography>} />
            </Tooltip>
          )}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed', mx: 1 }} />

        {location.pathname !== RouterLinks.firmProfileIncomplete && hasActivePlan && (
          <Stack m={2} onClick={handleSettings} sx={{ cursor: 'pointer' }}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <GearIcon />
              <Typography id="settings">Settings</Typography>
            </Stack>

            <Typography variant="caption" color={'text.disabled'} pt={1} id="settings-content">
              Customise your firm's branding, manage user roles and permissions, configure system integrations, and adjust notification preferences to ensure
              smooth and efficient operations.
            </Typography>
          </Stack>
        )}

        <Divider sx={{ borderStyle: 'dashed', mx: 1 }} />

        <Stack m={2} sx={{ cursor: 'pointer' }} onClick={handleThemeToggle}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              {settings.themeMode === 'dark' ? <LightModeRoundedIcon fontSize="small" /> : <DarkModeRoundedIcon fontSize="small" />}
              <Typography id="display-theme">Display Theme</Typography>
            </Stack>
            <Chip size="small" variant="soft" label={settings.themeMode === 'dark' ? 'Dark' : 'Light'} />
          </Stack>

          <Typography variant="caption" color={'text.disabled'} pt={1} id="display-theme-content">
            Switch the portal between Zeta light mode and the Client 360 dark mode aesthetic.
          </Typography>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed', mx: 1 }} />

        <MenuItem onClick={handleLogout} sx={{ my: 1, mx: 2, fontWeight: 'fontWeightBold', color: 'error.main', justifyContent: 'center' }}>
          <LogOutIcon fill={theme.palette.error.main} />
          <Typography variant="body2" id="logout">
            {' '}
            Logout
          </Typography>
        </MenuItem>
      </CustomPopover>
    </>
  );
}
